import { useRef, useState } from "react"
import { useDispatch } from "react-redux"
import { resetExperienceBookingData, resetExperienceById, resetTripAvailableSlots } from "store/experiences"
import { cleanAssetAllocation } from "store/bookings"
import { EMPTY_EXPERIENCE_FORM_VALUES, EMPTY_EXPERIENCE_SELECTIONS, EXPERIENCE_FORM_FIELDS } from "../constants"

/**
 * New Booking's Added Experiences (redesign, Figma 5795:173067). The experience being chosen stays in the form and bookingSelections,
 * as for a single booking; an added experience is a saved copy of them, so the Add Experience form
 * itself is unchanged.
 *
 * - "Add Another Experience" saves the experience on the form to the list (after `validateExperience`)
 *   and opens an empty Add Experience view (`sheet`), whose Confirm adds it and Cancel drops it.
 * - Edit puts an item back on the form in that view; Confirm replaces it, Cancel leaves it as it was.
 * - Remove drops an item; with none left the form is the empty Add Experience card again.
 * - The list shows only with two or more experiences: a single one left (after Cancel or Remove) goes
 *   back on the form, which then loads its experience the usual way — so Continue to Payment with
 *   one experience is today's single booking.
 *
 * `formKey` remounts the Add Experience form whenever the experience on it is swapped, so its own
 * state starts from the restored (or empty) selection; `onFormReplaced` lets the form drop state of
 * its own (the date error).
 */
const useAddedExperiences = ({
  form,
  bookingSelections,
  updateBookingSelections,
  experienceDetail,
  validateExperience,
  onFormReplaced
}) => {
  const dispatch = useDispatch()
  const [items, setItems] = useState([])
  // null: no Add/Edit view; { editingKey: null } adds a new one; { editingKey } edits that item.
  const [sheet, setSheet] = useState(null)
  const [formKey, setFormKey] = useState(0)
  const nextItemKeyRef = useRef(1)

  const putOnForm = (values, selections) => {
    form.setFieldsValue({ ...EMPTY_EXPERIENCE_FORM_VALUES, ...values })
    updateBookingSelections({ ...EMPTY_EXPERIENCE_SELECTIONS, ...selections })
    dispatch(resetExperienceBookingData())
    dispatch(resetTripAvailableSlots())
    dispatch(cleanAssetAllocation())
    dispatch(resetExperienceById())
    onFormReplaced?.()
    setFormKey((key) => key + 1)
  }

  const clearForm = () => putOnForm({}, {})

  const saveFormAs = (key) => ({
    key,
    values: form.getFieldsValue(EXPERIENCE_FORM_FIELDS),
    selections: bookingSelections,
    experience: experienceDetail
  })

  const addAnother = async () => {
    if (items.length === 0) {
      if (!(await validateExperience())) return
      setItems([saveFormAs(nextItemKeyRef.current++)])
    }
    clearForm()
    setSheet({ editingKey: null })
  }

  const confirm = async () => {
    if (!(await validateExperience())) return
    const editingKey = sheet?.editingKey
    setItems((current) =>
      editingKey ? current.map((item) => (item.key === editingKey ? saveFormAs(editingKey) : item)) : [...current, saveFormAs(nextItemKeyRef.current++)]
    )
    clearForm()
    setSheet(null)
  }

  // A list of one goes back on the form; an empty list leaves the form empty.
  const settle = (remaining) => {
    if (remaining.length === 1) {
      putOnForm(remaining[0].values, remaining[0].selections)
      setItems([])
      return
    }
    setItems(remaining)
  }

  const cancel = () => {
    setSheet(null)
    if (items.length === 1) settle(items)
    else clearForm()
  }

  const edit = (key) => {
    const item = items.find((candidate) => candidate.key === key)
    if (!item) return
    putOnForm(item.values, item.selections)
    setSheet({ editingKey: key })
  }

  const remove = (key) => settle(items.filter((item) => item.key !== key))

  return {
    items,
    sheet,
    formKey,
    isListShown: items.length > 0 && !sheet,
    addAnother,
    confirm,
    cancel,
    edit,
    remove
  }
}

export default useAddedExperiences
